import qs from 'qs'
import { AlertStatus, ALERT_STATUS } from '@saasalerts/common/shared'
import { DropdownMenuLinkType } from '@/components/Basic'
import { Organization } from '@saasalerts/common/manage'

class ReportQueryStringBuilder {
  queryObject: {
    reportFilters: {
      startDate: string
      endDate: string
      organizationId?: string
    }
    table?: {
      globalFilter?: string
      columnFilters?: { id: string; value: string | string[] }[]
    }
  } = {
    reportFilters: {
      startDate: '',
      endDate: '',
    },
  }
  constructor(startDate: string, endDate: string) {
    this.queryObject = {
      reportFilters: {
        startDate,
        endDate,
      },
    }
  }

  getQueryString() {
    return qs.stringify(this.queryObject)
  }

  setGlobalTableFilter(label: string) {
    this.queryObject = {
      ...this.queryObject,
      table: {
        ...(this.queryObject?.table ?? {}),
        globalFilter: label,
      },
    }
  }

  setOrganizationIdReportFilter(organizationId: string) {
    this.queryObject = {
      ...this.queryObject,
      reportFilters: {
        ...this.queryObject.reportFilters,
        organizationId: organizationId,
      },
    }
  }

  setTableFilter(filter: { id: string; value: string | string[] }) {
    this.queryObject = {
      ...this.queryObject,
      table: {
        ...(this.queryObject?.table ?? {}),
        columnFilters: [filter],
      },
    }
  }
}

export interface IChartDrilldown {
  labels: string[]
  startDate: string
  endDate: string
  alertStatus: AlertStatus
  organizations?: Array<Organization>
}

const getOrganizationIdByOrganizationName = (
  name: string,
  organizations: Array<Organization>
): string | undefined => {
  return (
    organizations.find((organization) => organization.name === name)?.id ??
    undefined
  )
}

const getOrganizationAlertOption = (
  label: string,
  alertStatus: AlertStatus,
  queryString: string
) => {
  const option = {
    title: label,
    href: `reports/${alertStatus}-customer-alerts?${queryString}`,
  }

  return option
}

const getFileShareOption = (label: string, queryString: string) => {
  const option = {
    title: label,
    href: `reports/file-share-events?${queryString}`,
  }

  return option
}

const buildOptionAll = (
  alertStatus: AlertStatus,
  startDate: string,
  endDate: string
): DropdownMenuLinkType => {
  const qsBuilder = new ReportQueryStringBuilder(startDate, endDate)
  return getOrganizationAlertOption(
    'All',
    alertStatus,
    qsBuilder.getQueryString()
  )
}

export const getCountryDrilldownOptions = (
  data: IChartDrilldown
): DropdownMenuLinkType[] => {
  const { labels, startDate, endDate, alertStatus } = data

  const options =
    labels.map((label) => {
      const qsBuilder = new ReportQueryStringBuilder(startDate, endDate)
      qsBuilder.setGlobalTableFilter(label)

      return getOrganizationAlertOption(
        label,
        alertStatus,
        qsBuilder.getQueryString()
      )
    }) ?? []

  return [buildOptionAll(alertStatus, startDate, endDate), ...options]
}

export const getAccountDrilldownOptions = (
  data: IChartDrilldown
): DropdownMenuLinkType[] => {
  return getCountryDrilldownOptions(data)
}

export const getOrganizationDrilldownOptions = (
  data: IChartDrilldown
): DropdownMenuLinkType[] => {
  const { labels, startDate, endDate, alertStatus, organizations } = data
  const options =
    labels.map((label) => {
      const qsBuilder = new ReportQueryStringBuilder(startDate, endDate)
      qsBuilder.setOrganizationIdReportFilter(
        getOrganizationIdByOrganizationName(label, organizations ?? []) ?? ''
      )
      qsBuilder.setTableFilter({ id: 'organization', value: label })

      return getOrganizationAlertOption(
        label,
        alertStatus,
        qsBuilder.getQueryString()
      )
    }) ?? []

  /**
   * when organization alertStatus is low then return organizations without All option
   * because in low critical alerts report we do not accept 'All' option in organizations dropdown list
   */
  if (alertStatus === ALERT_STATUS.LOW) {
    return options
  }

  return [buildOptionAll(alertStatus, startDate, endDate), ...options]
}

export const getFileShareEventDrilldownOptions = (
  data: IChartDrilldown
): DropdownMenuLinkType[] => {
  const { labels, startDate, endDate, organizations } = data

  const qsBuilder = new ReportQueryStringBuilder(startDate, endDate)
  const options =
    labels.map((label) => {
      const qsBuilder = new ReportQueryStringBuilder(startDate, endDate)
      qsBuilder.setOrganizationIdReportFilter(
        getOrganizationIdByOrganizationName(label, organizations ?? []) ?? ''
      )

      return getFileShareOption(label, qsBuilder.getQueryString())
    }) ?? []

  return [getFileShareOption('All', qsBuilder.getQueryString()), ...options]
}

export const getProductDrilldownOptions = (
  data: IChartDrilldown
): DropdownMenuLinkType[] => {
  const { labels, startDate, endDate, alertStatus } = data

  const options =
    labels.map((label) => {
      const qsBuilder = new ReportQueryStringBuilder(startDate, endDate)
      qsBuilder.setTableFilter({ id: 'product', value: [label] })

      return getOrganizationAlertOption(
        label,
        alertStatus,
        qsBuilder.getQueryString()
      )
    }) ?? []

  return [buildOptionAll(alertStatus, startDate, endDate), ...options]
}
